import { MapPin, Navigation } from 'lucide-react'
import { cn } from '@/lib/utils'

/** 반경 옵션 */
const RADIUS_OPTIONS = [
  { value: 3, label: '3km' },
  { value: 5, label: '5km' },
  { value: 10, label: '10km' },
  { value: 20, label: '20km' },
] as const

interface LocationBarProps {
  /** 현재 위치 표시명 (주소) */
  locationName: string | null
  /** 현재 반경 (km) */
  radiusKm: number
  /** 위치 변경 버튼 클릭 */
  onChangeLocation: () => void
  /** 현재 위치로 재설정 */
  onResetToGps: () => void
  /** 반경 변경 */
  onRadiusChange: (radius: number) => void
  /** GPS 기반 위치인지 여부 */
  isGpsLocation: boolean
  /** 위치 로딩 중 여부 */
  isLoading: boolean
}

/**
 * 위치 표시 + 반경 선택 바 v2.0
 *
 * - 카드 스타일 (그림자 + 라운드)
 * - 아이콘: 더 크고 컬러풀
 * - 반경 선택: 필 스타일 버튼
 */
export default function LocationBar({
  locationName,
  radiusKm,
  onChangeLocation,
  onResetToGps,
  onRadiusChange,
  isGpsLocation,
  isLoading,
}: LocationBarProps) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-[var(--shadow-card)]">
      {/* 위치 표시 행 */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-cb-primary-light">
            <MapPin size={18} className="text-cb-primary" />
          </div>
          {isLoading ? (
            <span className="text-body-sm text-cb-gray-400">
              위치를 확인하고 있어요...
            </span>
          ) : locationName ? (
            <span className="truncate text-body-sm font-semibold text-cb-gray-800">
              {locationName}
            </span>
          ) : (
            <span className="text-body-sm text-cb-gray-400">
              위치 정보 없음
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {!isGpsLocation && (
            <button
              onClick={onResetToGps}
              className="flex items-center gap-1.5 rounded-full bg-cb-gray-50 px-3 py-1.5 text-xs font-medium text-cb-gray-600 hover:bg-cb-gray-100 transition-all duration-200 shadow-sm"
              aria-label="현재 위치로 재설정"
            >
              <Navigation size={12} />
              현위치
            </button>
          )}
          <button
            onClick={onChangeLocation}
            className="rounded-full bg-cb-primary-light px-3 py-1.5 text-xs font-semibold text-cb-primary hover:bg-cb-primary-muted transition-all duration-200 shadow-sm"
            aria-label="위치 변경"
          >
            위치 변경
          </button>
        </div>
      </div>

      {/* 반경 선택 행 */}
      {locationName && (
        <div className="mt-3 flex items-center gap-2.5">
          <span className="text-xs font-medium text-cb-gray-500 flex-shrink-0">반경</span>
          <div className="flex gap-2" role="radiogroup" aria-label="검색 반경 선택">
            {RADIUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => onRadiusChange(option.value)}
                className={cn(
                  'rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-200',
                  radiusKm === option.value
                    ? 'bg-cb-primary text-white shadow-md shadow-cb-primary/20'
                    : 'bg-cb-gray-50 text-cb-gray-600 hover:bg-cb-gray-100'
                )}
                role="radio"
                aria-checked={radiusKm === option.value}
                aria-label={`반경 ${option.label}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
